import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Phone, 
  Globe, 
  CheckCircle, 
  MessageCircle, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Mail,
  Building2,
  Users,
  ExternalLink,
  Languages,
  Play,
  Star
} from 'lucide-react';
import { CompanyReviewsSection } from '@/components/reviews/CompanyReviewsSection';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useCategoryHelpers } from '@/hooks/useCategoryHelpers';
import { SocialShareDropdown } from '@/components/SocialShareDropdown';
import EventLocationMap from '@/components/events/EventLocationMap';
import LoginGate from '@/components/auth/LoginGate';
import { SEO } from '@/components/SEO';
import { WORLD_LANGUAGES } from '@/data/languages';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { useContactTracking } from '@/hooks/useContactTracking';
import { Badge } from '@/components/ui/badge';
import { sanitizeHtml } from '@/lib/sanitize';

// Helper to get YouTube embed URL
const getYouTubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;
  
  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return null;
};

// Helper to get language flag by name
const getLanguageFlag = (langName: string): string => {
  const lang = WORLD_LANGUAGES.find(l => l.name === langName);
  return lang?.flag || '🌐';
};

interface TeamMember {
  id: string;
  name: string;
  role_it: string | null;
  role_en: string | null;
  role_es: string | null;
  bio_it: string | null;
  bio_en: string | null;
  bio_es: string | null;
  photo_url: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  position: number;
  is_visible: boolean;
}

interface Company {
  id: string;
  name: string;
  slug: string;
  tagline_it: string | null;
  tagline_en: string | null;
  tagline_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  video_url: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  website: string | null;
  address_street: string | null;
  address_city: string | null;
  address_zip: string | null;
  address_region: string | null;
  address_country: string | null;
  latitude: number | null;
  longitude: number | null;
  social_facebook: string | null;
  social_instagram: string | null;
  social_linkedin: string | null;
  categories: string[] | null;
  languages_spoken: string[] | null;
  is_verified: boolean;
  vat_number: string | null;
}

const CompanyProfile = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { trackContactClick } = useContactTracking();
  const { getCategoriesByIds, getCategoryName } = useCategoryHelpers();
  const [company, setCompany] = useState<Company | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  const handleContactClick = (contactType: 'email' | 'phone' | 'whatsapp' | 'website' | 'facebook' | 'instagram' | 'linkedin') => {
    if (company?.id) {
      trackContactClick({ companyId: company.id, contactType });
    }
  };

  useEffect(() => {
    loadCompany();
  }, [slug]);

  const loadCompany = async () => {
    try {
      // Fetch company by slug
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (companyError) throw companyError;
      if (!companyData) {
        navigate('/404');
        return;
      }

      setCompany(companyData);

      // Fetch team members
      const { data: membersData, error: membersError } = await supabase
        .from('company_team_members')
        .select('*')
        .eq('company_id', companyData.id)
        .eq('is_visible', true)
        .order('position', { ascending: true });

      if (!membersError && membersData) {
        setTeamMembers(membersData);
      }
    } catch (error: any) {
      console.error('Error loading company:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
      navigate('/404');
    } finally {
      setLoading(false);
    }
  };

  const getTagline = () => {
    if (!company) return '';
    if (i18n.language === 'it') return company.tagline_it;
    if (i18n.language === 'en') return company.tagline_en || company.tagline_it;
    if (i18n.language === 'es') return company.tagline_es || company.tagline_it;
    return company.tagline_it;
  };

  const getDescription = () => {
    if (!company) return '';
    if (i18n.language === 'it') return company.description_it;
    if (i18n.language === 'en') return company.description_en || company.description_it;
    if (i18n.language === 'es') return company.description_es || company.description_it;
    return company.description_it;
  };

  const getMemberRole = (member: TeamMember) => {
    if (i18n.language === 'it') return member.role_it;
    if (i18n.language === 'en') return member.role_en || member.role_it;
    if (i18n.language === 'es') return member.role_es || member.role_it;
    return member.role_it;
  };

  const getMemberBio = (member: TeamMember) => {
    if (i18n.language === 'it') return member.bio_it;
    if (i18n.language === 'en') return member.bio_en || member.bio_it;
    if (i18n.language === 'es') return member.bio_es || member.bio_it;
    return member.bio_it;
  };

  const formatFullAddress = () => {
    if (!company) return null;
    const parts = [];
    
    if (company.address_street) parts.push(company.address_street);
    
    const cityLine = [];
    if (company.address_zip) cityLine.push(company.address_zip);
    if (company.address_city) cityLine.push(company.address_city);
    if (company.address_region) cityLine.push(`(${company.address_region})`);
    if (cityLine.length > 0) parts.push(cityLine.join(' '));
    
    if (company.address_country) {
      const countryNames: Record<string, string> = {
        'IT': 'Italia',
        'ES': 'España',
        'US': 'United States',
        'UK': 'United Kingdom',
        'DE': 'Deutschland',
        'FR': 'France',
      };
      parts.push(countryNames[company.address_country] || company.address_country);
    }
    
    return parts.length > 0 ? parts : null;
  };

  const hasAddress = company && (company.address_street || company.address_city);
  const hasMap = company && company.latitude && company.longitude;
  const addressParts = formatFullAddress();

  // SEO: Generate dynamic meta data for company - MUST be before conditional returns
  const seoData = useMemo(() => {
    if (!company) {
      return { title: '', description: '', schema: null };
    }
    
    const lang = i18n.language.substring(0, 2) as 'it' | 'en' | 'es';
    const tagline = company.tagline_it || company.tagline_en || company.tagline_es || '';
    const categories = (company.categories || [])
      .map((catId: string) => getCategoryName(catId, lang))
      .slice(0, 3)
      .join(', ');
    
    const location = company.address_city || '';
    
    const title = `${company.name} - ${lang === 'it' ? 'Azienda Inclusiva' : lang === 'es' ? 'Empresa Inclusiva' : 'Inclusive Company'}`;
    const description = `${company.name}${tagline ? ` - ${tagline}` : ''}. ${lang === 'it' ? 'Settori' : lang === 'es' ? 'Sectores' : 'Sectors'}: ${categories}${location ? `. ${lang === 'it' ? 'Sede' : lang === 'es' ? 'Sede' : 'Location'}: ${location}` : ''}. ${lang === 'it' ? 'Azienda inclusiva verificata su Inclusia.' : lang === 'es' ? 'Empresa inclusiva verificada en Inclusia.' : 'Inclusive company verified on Inclusia.'}`;
    
    const companyDescription = company.description_it || company.description_en || company.description_es || '';
    
    const schema = {
      "@context": "https://schema.org",
      "@type": ["Organization", "LocalBusiness"],
      "name": company.name,
      "description": companyDescription,
      "image": company.logo_url,
      "url": `${window.location.origin}/azienda/${company.slug}`,
      ...(company.website && { "sameAs": company.website }),
      ...(company.address_city && {
        "address": {
          "@type": "PostalAddress",
          ...(company.address_street && { "streetAddress": company.address_street }),
          "addressLocality": company.address_city,
          ...(company.address_region && { "addressRegion": company.address_region }),
          ...(company.address_zip && { "postalCode": company.address_zip }),
          ...(company.address_country && { "addressCountry": company.address_country })
        }
      }),
      ...(company.latitude && company.longitude && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": company.latitude,
          "longitude": company.longitude
        }
      }),
      ...(company.phone && { "telephone": company.phone }),
      ...(company.email && { "email": company.email })
    };
    
    return { title, description, schema };
  }, [company, i18n.language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="border-[3px] border-dark bg-surface shadow-[8px_8px_0px_0px] shadow-dark p-8">
              <div className="flex items-start gap-6">
                <Skeleton className="w-40 h-40 rounded-full" />
                <div className="flex-1 space-y-4">
                  <Skeleton className="h-10 w-2/3" />
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </div>
              <div className="mt-8 space-y-4">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoData.title}
        description={seoData.description}
        image={company.logo_url || undefined}
        type="website"
        lang={i18n.language.substring(0, 2) as 'it' | 'en' | 'es'}
        schema={seoData.schema}
      />
      <Header />
      
      {/* Cover Image - Full Width (come professionista) */}
      {company.cover_image_url && (
        <div className="w-full">
          <img
            src={company.cover_image_url}
            alt={`${company.name} cover`}
            className="w-full h-auto border-b-[3px] border-dark"
            loading="lazy"
          />
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Single Container for All Sections */}
          <div className="border-[3px] border-dark bg-surface shadow-[8px_8px_0px_0px] shadow-dark">
            
            {/* Company Header */}
            <div className="px-6 md:px-8 py-4 md:py-6">
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Logo */}
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    className="w-40 h-40 rounded-full border-[4px] border-dark object-cover bg-white shadow-[8px_8px_0px_0px] shadow-dark"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-40 h-40 rounded-full border-[4px] border-dark bg-muted shadow-[8px_8px_0px_0px] shadow-dark flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
                
                <div className="flex-1 w-full">
                  {/* Name and Verified Badge */}
                  <div className="mb-4">
                    <SEOBreadcrumb 
                      items={[
                        { label: t('common.professionals'), href: '/professionals' },
                        { label: company.name }
                      ]}
                      align="left"
                    />
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h1 className="font-heading text-3xl md:text-5xl font-black text-dark">
                        {company.name}
                      </h1>
                      <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs px-2 py-1 border-0 uppercase font-bold tracking-wider">
                        {t('professionals.companyBadge')}
                      </Badge>
                      {company.is_verified && (
                        <CheckCircle className="w-7 h-7 text-primary flex-shrink-0" />
                      )}
                    </div>
                    {getTagline() && (
                      <p className="text-lg text-muted-foreground font-medium">{getTagline()}</p>
                    )}
                  </div>

                  {/* Categories */}
                  {company.categories && company.categories.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap mb-4">
                      <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {t('company.categories', 'Settori')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {getCategoriesByIds(company.categories).map((cat) => (
                          <span 
                            key={cat.id} 
                            className="px-3 py-1 bg-primary/10 border border-dark/20 text-sm font-medium"
                          >
                            {cat?.[i18n.language.substring(0, 2) as 'it' | 'en' | 'es']?.name || cat?.it?.name || cat?.id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages - Inline come professionista */}
                  {company.languages_spoken && company.languages_spoken.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        {t('company.languages', 'Lingue')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {company.languages_spoken.map((langName: string) => (
                          <span 
                            key={langName} 
                            className="px-3 py-1 bg-accent/10 border border-dark/20 text-sm font-medium"
                          >
                            {langName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <LoginGate 
                    title={t('auth.gate.viewCompanyContactsAndLocation')} 
                    description={t('auth.gate.registerFreeToSeeContacts')}
                    variant="inline"
                  >
                    {/* Contacts */}
                    <div className="mt-6">
                      <h2 className="font-heading text-xl font-bold mb-3">
                        {t('profile.contactsOf', 'Contatti di {{name}}', { name: company.name })}
                      </h2>
                      <TooltipProvider>
                        <div className="flex flex-wrap items-center gap-3">
                          {company.phone && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={`tel:${company.phone}`}
                                  onClick={() => handleContactClick('phone')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Phone className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('profile.call', 'Chiama')}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.email && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={`mailto:${company.email}`}
                                  onClick={() => handleContactClick('email')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Mail className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Email</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.whatsapp && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={`https://wa.me/${company.whatsapp}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleContactClick('whatsapp')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <MessageCircle className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>WhatsApp</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.website && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleContactClick('website')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Globe className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('profile.website', 'Sito web')}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.social_facebook && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={company.social_facebook}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleContactClick('facebook')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Facebook className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Facebook</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.social_instagram && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={company.social_instagram}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleContactClick('instagram')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Instagram className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Instagram</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {company.social_linkedin && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={company.social_linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleContactClick('linkedin')}
                                  className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                >
                                  <Linkedin className="w-5 h-5" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>LinkedIn</p>
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {/* Share Dropdown */}
                          <div className="ml-auto">
                            <SocialShareDropdown 
                              url={window.location.href}
                              title={company.name}
                            />
                          </div>
                        </div>
                      </TooltipProvider>
                    </div>

                    {/* Sede Box - sotto i contatti */}
                    {hasAddress && (
                      <div className="mt-6 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark p-4 md:p-6">
                        <div className={`grid gap-4 ${hasMap ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                          {/* Colonna Sinistra - Titolo + Indirizzo */}
                          <div>
                            <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {t('company.location', 'Sede')}
                            </h3>
                            <div className="space-y-1">
                              {addressParts?.map((line, index) => (
                                <p key={index} className={index === 0 ? 'font-semibold' : 'text-muted-foreground text-sm'}>
                                  {line}
                                </p>
                              ))}
                              {company.vat_number && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  P.IVA: {company.vat_number}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Colonna Destra - Mappa */}
                          {hasMap && (
                            <div className="h-40 md:h-full min-h-[160px]">
                              <EventLocationMap 
                                latitude={company.latitude!}
                                longitude={company.longitude!}
                                locationName={company.name}
                                locationAddress={company.address_street || undefined}
                                locationCity={company.address_city || undefined}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </LoginGate>

                  {/* Chi Siamo - dentro header section come professionista */}
                  {getDescription() && (
                    <div className="mt-6">
                      <h2 className="font-heading text-xl font-bold mb-3">
                        {t('company.aboutCompany', 'Su {{name}}', { name: company.name })}
                      </h2>
                      <div className="prose prose-sm max-w-none font-body">
                        <div 
                          className="text-muted-foreground leading-relaxed"
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtml(getDescription() || '', {
                              allowedTags: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'h2', 'blockquote']
                            }) 
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Video - dentro header section come professionista */}
                  {company.video_url && getYouTubeEmbedUrl(company.video_url) && (
                    <div className="mt-6">
                      <div className="relative w-full aspect-video border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark overflow-hidden">
                        <iframe
                          src={getYouTubeEmbedUrl(company.video_url)!}
                          title={`${company.name} video`}
                          className="absolute inset-0 w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  {/* Team Section - dentro header section come professionista */}
                  {teamMembers.length > 0 && (
                    <div className="mt-6">
                      <h2 className="font-heading text-xl font-bold mb-1">
                        {t('profile.teamOf', 'Il team di {{name}}', { name: company.name })}
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        {t('profile.teamDescription', 'Scopri le persone che fanno parte, e rendono, {{name}} una realtà inclusiva.', { name: company.name })}
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {teamMembers.map((member) => (
                          <div 
                            key={member.id}
                            className="border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark p-4"
                          >
                            {/* Member Photo */}
                            <div className="flex items-center gap-4 mb-3">
                              {member.photo_url ? (
                                <img
                                  src={member.photo_url}
                                  alt={member.name}
                                  className="w-16 h-16 rounded-full object-cover border-[3px] border-dark"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full border-[3px] border-dark bg-muted flex items-center justify-center">
                                  <span className="text-2xl font-bold text-muted-foreground">
                                    {member.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <h3 className="font-heading font-bold text-lg truncate">{member.name}</h3>
                                {getMemberRole(member) && (
                                  <p className="text-sm text-muted-foreground italic">{getMemberRole(member)}</p>
                                )}
                                {/* LinkedIn Button */}
                                {member.linkedin_url && (
                                  <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2">
                                    <BrutalButton variant="secondary" size="sm" className="normal-case font-medium tracking-normal px-3 py-1 text-xs shadow-[4px_4px_0px_0px] hover:shadow-[2px_2px_0px_0px]">
                                      {t('profile.meetTeamMember', 'Conosci il membro del team')}
                                    </BrutalButton>
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Separator before Reviews */}
            <Separator className="h-[3px] bg-dark" />

            {/* Reviews Section - Full Width */}
            <div className="px-6 md:px-8 py-4 md:py-6">
              <CompanyReviewsSection 
                companyId={company.id} 
                companyName={company.name} 
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyProfile;
